import 'bootstrap/dist/css/bootstrap.min.css';
import "./App.css";

import React, { Suspense, useEffect, useState } from "react";
import { Route, Switch, useLocation } from "react-router-dom";

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import { config } from "./config";
import axios from "axios";

import Nav from "./components/Nav";
import Popup from "./components/Popup";
import SubscriptionBanner from "./components/SubscriptionBanner";
import MoreInfo from "./components/MoreInfo";
import Tracking from "./components/Tracking";
import CookieBanner from "./components/Tracking/CookieBanner";
import Loader from "./components/Loader";

import Auth from "./lib/Auth";
import Head from "./lib/Head";
import Newsletter from "./lib/Newsletter";
import Redirections from './lib/Redirections';
import { initMixpanel } from './lib/mixpanelClient';

import { selectMainDomain, selectWhitelabelDomain } from './redux/selectors/whitelabel';
import { loadWhitelabelInfo } from './redux/actions/whitelabel';

// Lazy-loaded Route Components
const Home = React.lazy(() => import(/* webpackChunkName: "home" */ "./pages/Homes/Home"));
const BikefittingPage = React.lazy(() => import(/* webpackChunkName: "home" */ "./pages/BikefittingPage"));

const Contact = React.lazy(() => import(/* webpackChunkName: "static" */ "./pages/Contact"));
const ConfirmAccount = React.lazy(() => import(/* webpackChunkName: "auth" */ "./pages/ConfirmAccount"));
const ResetPassword = React.lazy(() => import(/* webpackChunkName: "auth" */ "./pages/ResetPassword"));
const Faq = React.lazy(() => import(/* webpackChunkName: "static" */ "./pages/Faq"));
const Pricing = React.lazy(() => import(/* webpackChunkName: "static" */ "./pages/Pricing"));
const Cookies = React.lazy(() => import(/* webpackChunkName: "static" */ "./pages/Cookies"));
const NoCookies = React.lazy(() => import(/* webpackChunkName: "static" */ "./pages/NoCookies"));
const BikePartners = React.lazy(() => import(/* webpackChunkName: "static" */ "./pages/BikefitPartners"));

const FramemaatAdvice = React.lazy(() => import(/* webpackChunkName: "advice" */ "./pages/FramemaatAdvice"));
const CockpitAdvice = React.lazy(() => import(/* webpackChunkName: "advice" */ "./pages/CockpitAdvice"));
const BikeComperision = React.lazy(() => import(/* webpackChunkName: "advice" */ "./pages/BikeComparision"));

const InfoMtbTypes = React.lazy(() => import(/* webpackChunkName: "info" */ "./pages/Info/InfoMtbTypes"));
const InfoSuspension = React.lazy(() => import(/* webpackChunkName: "info" */ "./pages/Info/InfoSuspension"));
const InfoPosture = React.lazy(() => import(/* webpackChunkName: "info" */ "./pages/Info/InfoPosture"));
const InfoFineTune = React.lazy(() => import(/* webpackChunkName: "info" */ "./pages/Info/InfoFineTune"));
const InfoFramefitFineTune = React.lazy(() => import(/* webpackChunkName: "info" */ "./pages/Info/InfoFramefitFineTune"));
const InfoMeasureDefinitions = React.lazy(() => import(/* webpackChunkName: "info" */ "./pages/Info/InfoMeasureDefinitions"));
const InfoFitScore = React.lazy(() => import(/* webpackChunkName: "info" */ "./pages/Info/InfoFitScore"));
const InfoDisclaimer = React.lazy(() => import(/* webpackChunkName: "info" */ "./pages/Info/InfoDisclaimer"));
const ReviewPage = React.lazy(() => import(/* webpackChunkName: "info" */ "./pages/Info/infoReview"));

const BlogOverview = React.lazy(() => import(/* webpackChunkName: "blog" */ "./pages/Blog/BlogOverview"));
const OtherBlogs = React.lazy(() => import(/* webpackChunkName: "blog" */ "./pages/Blog/BlogOverview/OtherBlogs"));
const SingleBlog = React.lazy(() => import(/* webpackChunkName: "blog" */ "./pages/Blog/SingleBlog"));

const Trajectory = React.lazy(() => import(/* webpackChunkName: "routes" */ './components/Bikefinder/Trajectory'));
const BikefitRouter = React.lazy(() => import(/* webpackChunkName: "routes" */ './components/Bikefit/BikefitRouter'));
const EbikeRouter = React.lazy(() => import(/* webpackChunkName: "routes" */ './components/Ebike/Routes'));

const SubscriptionRoutes = React.lazy(() => import(/* webpackChunkName: "subscription" */ "./components/Subscription").then(mod => ({ default: mod.Routes })));

const NotFound = React.lazy(() => import(/* webpackChunkName: "fallback" */ "./pages/NotFound"));

axios.defaults.baseURL = config.rootUrl;

const App = (props) => {
  const location = useLocation();
  const dispatch = useDispatch()

  const [t,] = useTranslation("common")
  const [loading, setLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const isWhiteLabelSubdomain = useSelector(selectWhitelabelDomain);
  const isMainDomain = useSelector(selectMainDomain);

  useEffect(() => {
    initMixpanel()
  }, [])

  useEffect(
    () => {
      Head.addHreflangLinks()
      Head.addCanonicalTag()
    },
    [location]
  )

  useEffect(() => {
    dispatch(loadWhitelabelInfo())
  }, [loadWhitelabelInfo])

  // Redirect to /bikefitting if not on main domain or subdomain with /bikefitting or /bikefit
  useEffect(() => {
    let timeout;

    if (isMainDomain) {
      timeout = setTimeout(() => {
        setLoading(false);
      }, 1000);
    } else if (isWhiteLabelSubdomain) {
      if (
          location.pathname.includes("/img") ||
          location.pathname.includes("/static") ||
          location.pathname.includes("/bikefit")
      ) {
        setLoading(false);
      } else {
        Redirections.replaceTo("/bikefit/bike-type");
      }
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isMainDomain, isWhiteLabelSubdomain, location.pathname]);

  useEffect(() => {
    if (Auth.isLoggedIn()) {
      Auth.checkLogin()
        .then(() => {
          Newsletter.fetchLocalEntry()
        })
        .catch(status => {
          if (status === 401) {
            alert(t('SessionExpired'))
          }
          Auth.clearData();
          window.location.reload()
        })
    }
  });

  useEffect(() => {
    const img = new Image();
    img.src = "/img/home/online-bike-fit.jpg";

    img.onload = () => {
      setIsLoaded(true);
    };

    document.fonts.ready.then(() => {
      setLoading(false);
    });
  }, []);

  if (loading && !isLoaded) {
    return <Loader />
  }

  return (
    <Suspense fallback={<Loader />}>
      <SubscriptionBanner />
      <Nav loading={loading} setLoading={setLoading} />
      <Popup />
      <MoreInfo />
      <Tracking history={props.history} />
      <Switch>

        <Route exact path="(/...?/..)?/" render={() => <Home isLoaded={isLoaded} />} />
        <Route exact path="(/...?/..)?/bikefitting" render={() => <BikefittingPage />} />
        <Route
          path="(/...?/..)?/contact"
          render={() => <Contact history={props.history} />}
        />
        <Route
          path="(/...?/..)?/confirm"
          render={() => <ConfirmAccount history={props.history} />}
        />

        <Route
          path="(/...?/..)?/reset"
          render={() => <ResetPassword history={props.history} />}
        />
        <Route path="(/...?/..)?/faq" render={() => <Faq />} />
        <Route path="(/...?/..)?/pricing" render={() => <Pricing />} />
        <Route path="(/...?/..)?/cookie-and-data-setting" render={() => <Cookies />} />
        <Route path="(/...?/..)?/bikepartners" render={() => <BikePartners />} />

        <Route path="(/...?/..)?/info/mtb-types" render={() => <InfoMtbTypes />} />
        <Route path="(/...?/..)?/info/suspension" render={() => <InfoSuspension />} />
        <Route path="(/...?/..)?/info/posture" render={() => <InfoPosture />} />
        <Route path="(/...?/..)?/info/fine-tune" render={() => <InfoFineTune />} />
        <Route
          path="(/...?/..)?/info/framefit-fine-tune"
          render={() => <InfoFramefitFineTune />}
        />
        <Route
          path="(/...?/..)?/info/measure-definitions"
          render={() => <InfoMeasureDefinitions />}
        />
        <Route path="(/...?/..)?/info/fitscore" render={() => <InfoFitScore />} />
        <Route path="(/...?/..)?/info/disclaimer" render={() => <InfoDisclaimer />} />
        <Route
          path="(/...?/..)?/info/review"
          render={() => <ReviewPage history={props.history} />}
        />

        <Route path="(/...?/..)?/subscription" component={SubscriptionRoutes} />
        <Route path="(/...?/..)?/nocookies" component={NoCookies} />
        <Route path="(/...?/..)?/compare-bikes" component={BikeComperision} />
        <Route path="(/...?/..)?/frame-advice" component={FramemaatAdvice} />
        <Route path="(/...?/..)?/cockpit-advice" component={CockpitAdvice} />

        <Route path="(/...?/..)?/mtb"
          component={({ match }) => (<Trajectory traject="mtb" path={match.url} history={props.history} />)} />
        <Route path="(/...?/..)?/road"
          component={({ match }) => (<Trajectory traject="race" path={match.url} history={props.history} />)} />
        <Route path="(/...?/..)?/bikefit" component={BikefitRouter} />
        <Route path="(/...?/..)?/landing/bikefit" component={BikefitRouter} />
        <Route path="(/...?/..)?/ebike" component={EbikeRouter} />

        <Route path="(/...?/..)?/blog" component={BlogOverview} />
        <Route path="(/...?/..)?/other-blogs" component={OtherBlogs} />

        <Route path="(/...?/..)?/:slug" component={SingleBlog} />
        <Route component={NotFound} />

      </Switch>
      <CookieBanner />
    </Suspense>
  );
};

export default App;
